import React, { useCallback, useEffect, useState } from 'react';
import { FaBuilding, FaChevronLeft, FaHome, FaBullhorn, FaPlus, FaSync } from 'react-icons/fa';
import { fetchProjects, fetchProjectDetail } from '../utils/inventoryWizardApi';
import { getUnitTypeLabel } from '../utils/unitTypeSpecs';
import InventoryWizard from './InventoryWizard';
import { resolveCoverImage } from '../utils/propertyCovers';
import './ProjectHub.css';

const statusLabel = (status, isAr) => {
  const map = {
    vacant: [isAr ? 'شاغرة' : 'Vacant', '#059669'],
    rented: [isAr ? 'مؤجرة' : 'Rented', '#2563eb'],
    maintenance: [isAr ? 'صيانة' : 'Maintenance', '#d97706'],
    reserved: [isAr ? 'محجوزة' : 'Reserved', '#7c3aed'],
    sold: [isAr ? 'مباعة' : 'Sold', '#6b7280'],
  };
  return map[status] || [status, '#6b7280'];
};

export default function ProjectHub({ language, onBack }) {
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchProjects();
      setProjects(rows);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const data = await fetchProjectDetail(id);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const handleWizardSuccess = (result) => {
    loadProjects();
    if (result?.projectId) {
      setSelectedId(result.projectId);
    }
  };

  if (selectedId && detail) {
    const projectListings = (detail.listings || []).filter((l) => l.project_id);
    const unitListings = (detail.listings || []).filter((l) => l.unit_id);
    const projectMeta = typeof detail.metadata === 'string'
      ? (() => { try { return JSON.parse(detail.metadata); } catch { return {}; } })()
      : (detail.metadata || {});
    const detailCover = resolveCoverImage({
      coverImageUrl: detail.cover_image_url,
      primaryUnitType: projectMeta.primary_unit_type || detail.units?.[0]?.unit_type,
      projectType: detail.project_type,
    });

    return (
      <div className="phub">
        <div className="phub__hero">
          <img src={detailCover} alt="" className="phub__hero-img" />
        </div>
        <header className="phub__header">
          <button type="button" className="phub__back" onClick={() => setSelectedId(null)}>
            <FaChevronLeft /> {t('كل المشاريع', 'All projects')}
          </button>
          <div className="phub__title-row">
            <h2>{detail.name}</h2>
            <button type="button" className="phub__refresh" onClick={() => loadDetail(selectedId)} title={t('تحديث', 'Refresh')}>
              <FaSync />
            </button>
          </div>
          <p className="phub__meta">
            {[detail.city, detail.district].filter(Boolean).join(' · ')}
          </p>
        </header>

        <div className="phub__stats">
          <div className="phub__stat"><span>{detail.property_count || 0}</span><small>{t('عقارات', 'Properties')}</small></div>
          <div className="phub__stat"><span>{detail.unit_count || 0}</span><small>{t('وحدات', 'Units')}</small></div>
          <div className="phub__stat phub__stat--accent"><span>{detail.vacant_count || 0}</span><small>{t('شاغر', 'Vacant')}</small></div>
          <div className="phub__stat"><span>{(detail.listings || []).length}</span><small>{t('إعلانات', 'Listings')}</small></div>
        </div>

        {detailLoading ? (
          <p className="phub__loading">{t('جارٍ التحميل…', 'Loading…')}</p>
        ) : (
          <>
            <section className="phub__section">
              <h3><FaBuilding /> {t('العقارات', 'Properties')}</h3>
              {(detail.properties || []).length === 0 ? (
                <p className="phub__empty">{t('لا عقارات بعد', 'No properties yet')}</p>
              ) : (
                <ul className="phub__list">
                  {(detail.properties || []).map((p) => (
                    <li key={p.id}>
                      <strong>{p.name}</strong>
                      <span>{p.unit_count || 0} {t('وحدة', 'units')} · {p.vacant_count || 0} {t('شاغر', 'vacant')}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="phub__section">
              <h3><FaHome /> {t('الوحدات', 'Units')}</h3>
              {(detail.units || []).length === 0 ? (
                <p className="phub__empty">{t('لا وحدات بعد', 'No units yet')}</p>
              ) : (
                <div className="phub__table-wrap">
                  <table className="phub__table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('النوع', 'Type')}</th>
                        <th>{t('الحالة', 'Status')}</th>
                        <th>{t('الإيجار', 'Rent')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.units || []).map((u) => {
                        const [sl, sc] = statusLabel(u.status, isAr);
                        return (
                          <tr key={u.id}>
                            <td className="mono">{u.unit_number}</td>
                            <td>{getUnitTypeLabel(u.unit_type, language)}</td>
                            <td><span className="phub__pill" style={{ color: sc }}>{sl}</span></td>
                            <td>{u.monthly_rent ? `${u.monthly_rent} ${t('ر.س', 'SAR')}` : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="phub__section">
              <h3><FaBullhorn /> {t('الإعلانات', 'Listings')}</h3>
              {(detail.listings || []).length === 0 ? (
                <p className="phub__empty">{t('لا إعلانات', 'No listings')}</p>
              ) : (
                <ul className="phub__list">
                  {projectListings.map((l) => (
                    <li key={`p-${l.id}`}>
                      <strong>{l.title || detail.name}</strong>
                      <span>{t('إعلان مشروع / حملة', 'Project campaign')} · {l.status}</span>
                    </li>
                  ))}
                  {unitListings.map((l) => (
                    <li key={`u-${l.id}`}>
                      <strong>{l.title || l.unit_number}</strong>
                      <span>{t('وحدة', 'Unit')} · {l.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="phub">
      <header className="phub__header phub__header--list">
        <div>
          {onBack && (
            <button type="button" className="phub__back" onClick={onBack}>
              <FaChevronLeft /> {t('رجوع للوحدات', 'Back to units')}
            </button>
          )}
          <h2>{t('المشاريع العقارية', 'Property projects')}</h2>
          <p className="phub__meta">{t('مجموعات الوحدات والتسويق على مستوى المشروع', 'Unit groups and project-level marketing')}</p>
        </div>
        <button type="button" className="phub__add" onClick={() => setShowWizard(true)}>
          <FaPlus /> {t('مشروع جديد', 'New project')}
        </button>
      </header>

      {loading ? (
        <p className="phub__loading">{t('جارٍ التحميل…', 'Loading…')}</p>
      ) : projects.length === 0 ? (
        <div className="phub__empty-card">
          <p>{t('لا مشاريع بعد. أنشئ مشروعاً من المعالج.', 'No projects yet. Create one with the wizard.')}</p>
          <button type="button" className="phub__add" onClick={() => setShowWizard(true)}>
            <FaPlus /> {t('إضافة مشروع', 'Add project')}
          </button>
        </div>
      ) : (
        <div className="phub__grid">
          {projects.map((p) => {
            const meta = typeof p.metadata === 'string' ? (() => { try { return JSON.parse(p.metadata); } catch { return {}; } })() : (p.metadata || {});
            const cover = resolveCoverImage({
              coverImageUrl: p.cover_image_url,
              primaryUnitType: meta.primary_unit_type,
              projectType: p.project_type,
            });
            return (
              <button key={p.id} type="button" className="phub__card" onClick={() => setSelectedId(p.id)}>
                <img className="phub__card-cover" src={cover} alt="" />
                <div className="phub__card-body">
                  <h3>{p.name}</h3>
                  <p>{[p.city, p.district].filter(Boolean).join(' · ') || '—'}</p>
                  <div className="phub__card-stats">
                    <span>{p.unit_count || 0} {t('وحدة', 'units')}</span>
                    <span className="phub__vacant">{p.vacant_count || 0} {t('شاغر', 'vacant')}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showWizard && (
        <InventoryWizard
          language={language}
          initialMode="project"
          onClose={() => setShowWizard(false)}
          onSuccess={handleWizardSuccess}
        />
      )}
    </div>
  );
}
